import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";

// Network states: "online" | "offline" | "weak"
const NetworkContext = createContext({ status: "online", isOnline: true, isWeak: false });

const PING_URL = `${process.env.REACT_APP_BACKEND_URL}/api/health`;
const PING_INTERVAL = 30000; // 30s
const PING_TIMEOUT = 8000;   // 8s — anything longer = weak
const WEAK_THRESHOLD = 3000;  // >3s response = weak signal

export function NetworkProvider({ children }) {
  const [status, setStatus] = useState(navigator.onLine ? "online" : "offline");
  const intervalRef = useRef(null);
  const mountedRef = useRef(true);

  const checkConnection = useCallback(async () => {
    if (!navigator.onLine) {
      if (mountedRef.current) setStatus("offline");
      return;
    }

    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), PING_TIMEOUT);
    const start = Date.now();

    try {
      await fetch(PING_URL, { method: "GET", signal: controller.signal, cache: "no-store" });
      const elapsed = Date.now() - start;
      if (mountedRef.current) {
        setStatus(elapsed > WEAK_THRESHOLD ? "weak" : "online");
      }
    } catch {
      if (mountedRef.current) {
        setStatus(navigator.onLine ? "weak" : "offline");
      }
    } finally {
      clearTimeout(timeout);
    }
  }, []);

  useEffect(() => {
    mountedRef.current = true;

    const handleOnline = () => { setStatus("online"); checkConnection(); };
    const handleOffline = () => setStatus("offline");

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    // Initial check
    checkConnection();
    intervalRef.current = setInterval(checkConnection, PING_INTERVAL);

    return () => {
      mountedRef.current = false;
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [checkConnection]);

  const value = {
    status,
    isOnline: status === "online",
    isWeak: status === "weak",
    isOffline: status === "offline",
    retry: checkConnection,
  };

  return <NetworkContext.Provider value={value}>{children}</NetworkContext.Provider>;
}

export function useNetwork() {
  return useContext(NetworkContext);
}
